import React from 'react';
import NavLink from 'react-router-dom/NavLink';
import { ColDef } from 'ag-grid-community';
import { format as formatValue } from 'erisxkit/client';
import Big from 'bignumber.js';
import trim from 'lodash/trim';
import {
  NANO_TO_MILLI_FACTOR,
  formatDateField,
} from '../../../metadata/riskOverviewMetadata';
import RiskAccountSummary from '../../../ts/models/Risk/RiskAccountSummary.model';
import SelectFilter from 'utils/agGrid/components/SelectFilter';
import settlementTypes from 'constants/settlementTypes';

const format = (value: unknown) => {
  return formatValue(value, {});
};

const columnDefs = (
  riskDataFromTcs = 'false',
): ColDef<RiskAccountSummary>[] => {
  const columns: ColDef<RiskAccountSummary>[] = [
    {
      headerName: 'Transaction Time',
      field: 'transactionTime',
      initialWidth: 200,
      sortable: true,
      filter: false,
      valueFormatter: ({ value }) => {
        const time = value?.toString();
        // times in backend are in nano seconds, we need to convert those to milliseconds
        const mils = parseInt(
          time?.substring(0, time.length - NANO_TO_MILLI_FACTOR),
        );

        return formatDateField(mils);
      },
    },
    {
      headerName: 'Member',
      field: 'member',
      initialWidth: 200,
    },
    {
      headerName: 'TCS Account',
      initialWidth: 150,
      valueGetter: ({ data }) => {
        return data?.tcsAccount || '';
      },
      cellRenderer: ({ data }: { data: RiskAccountSummary }) =>
        data?.tcsAccountId ? (
          <NavLink
            target="_blank"
            className="text-right mono"
            to={`/balances/${trim(data?.tcsAccountId || '')}`}
          >
            {data?.tcsAccount}
          </NavLink>
        ) : (
          ''
        ),
    },
    {
      headerName: 'TME Account',
      initialWidth: 150,
      valueGetter: ({ data }) => {
        return data?.tmeAccount || '';
      },
      cellRenderer: ({ data }: { data: RiskAccountSummary }) =>
        data?.tmeAccount && data?.snapshotId ? (
          <NavLink
            target="_blank"
            className="text-right mono"
            to={`/risk/${data?.snapshotId?.toString()}/${trim(
              data?.tmeAccount || '',
            )}/detail`}
          >
            {data?.tmeAccount}
          </NavLink>
        ) : (
          ''
        ),
    },
  ];

  if (riskDataFromTcs === 'false') {
    columns.push({
      headerName: 'Margin Exposure Limit',
      field: 'marginExposureLimit',
      initialWidth: 200,
      sortable: true,
    });
  }

  columns.push(
    {
      headerName: 'Total Balance',
      initialWidth: 150,
      valueFormatter: ({ data }) => format(data?.totalBalance),
      sortable: true,
    },
    {
      headerName: 'Pay/Collect',
      initialWidth: 150,
      valueGetter: ({ data }) => {
        const varBig = new Big(data?.variationMargin || 0);
        const cdfBig = new Big(data?.cdfFundingAmount || 0);
        const sum = varBig.plus(cdfBig);
        return sum.isNaN() ? '-' : format(sum.toString());
      },
    },
    {
      headerName: 'Span Margin',
      field: 'totalSpanRequirement',
      initialWidth: 150,
      valueFormatter: ({ data }) => format(data?.totalSpanRequirement),
    },
    {
      headerName: 'Span Spread',
      field: 'totalSpreadRequirement',
      initialWidth: 150,
      valueFormatter: ({ data }) => format(data?.totalSpreadRequirement),
    },
    {
      headerName: 'Total Equity',
      field: 'totalEquity',
      initialWidth: 150,
      valueFormatter: ({ data }) => format(data?.totalEquity),
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['equals'],
      },
    },
  );

  if (riskDataFromTcs === 'false') {
    columns.push({
      headerName: 'Margin Purchasing Power',
      field: 'marginPurchasingPower',
      initialWidth: 250,
      valueFormatter: ({ data }) => format(data?.marginPurchasingPower),
    });
  }

  columns.push(
    {
      headerName: 'Margin Excess Deficit',
      field: 'marginExcessDeficit',
      initialWidth: 200,
      valueFormatter: ({ data }) => format(data?.marginExcessDeficit),
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['equals'],
      },
    },
    {
      headerName: 'Long $ Exposure',
      field: 'longExposure',
      initialWidth: 200,
      valueFormatter: ({ data }) => format(data?.longExposure),
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['equals'],
      },
    },
    {
      headerName: 'Short $ Exposure',
      field: 'shortExposure',
      initialWidth: 200,
      valueFormatter: ({ data }) => format(data?.shortExposure),
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['equals'],
      },
    },
    {
      headerName: 'Net $ Exposure',
      field: 'netExposure',
      initialWidth: 200,
      valueFormatter: ({ data }) => format(data?.netExposure),
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['equals'],
      },
    },
    {
      headerName: 'Gross $ Exposure',
      field: 'grossExposure',
      initialWidth: 200,
      valueFormatter: ({ data }) => format(data?.grossExposure),
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['equals'],
      },
    },
    {
      headerName: 'Snapshot Type',
      field: 'snapshotType',
      initialWidth: 200,
      sortable: true,
      filter: SelectFilter,
      filterParams: {
        placeholder: 'Select Type',
        options: settlementTypes.map((type) => ({
          key: type,
          text: type,
          value: type,
        })),
      },
    },
    {
      headerName: 'Snapshot ID',
      field: 'snapshotId',
      initialWidth: 200,
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['equals'],
      },
    },
    {
      headerName: 'Inserted At',
      initialWidth: 150,
      valueFormatter: ({ data }) => formatDateField(data?.insertedAt),
    },
    {
      headerName: 'Gross Stress Exposure',
      field: 'grossStressExposure',
      initialWidth: 200,
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['equals'],
      },
    },
    {
      headerName: 'Stress Scenario',
      field: 'stressScenario',
      initialWidth: 200,
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['equals'],
      },
    },
    {
      headerName: 'Net Stress Exposure',
      field: 'netStressExposure',
      initialWidth: 200,
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['equals'],
      },
    },
    {
      headerName: 'Stress Exposure Limit',
      field: 'stressExposureLimit',
      initialWidth: 200,
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['equals'],
      },
    },
    {
      headerName: 'Risk Profile',
      field: 'riskProfile',
      initialWidth: 150,
      sortable: true,
    },
    {
      headerName: 'Margin Profile',
      field: 'marginProfile',
      initialWidth: 200,
    },
  );
  return columns;
};
export default columnDefs;
